import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  ArrowLeft,
  Clipboard,
  Download,
  PauseFill,
  PlayFill,
  Trash,
  XLg,
} from "react-bootstrap-icons";
import moment from "moment";
import "react-toastify/dist/ReactToastify.css";
import "../assets/css/DeviceHealthCheck.css";
import { getDeviceMqttTopics } from "../commonApi";
import { kc } from "../keycloakConfig";

// Admin MQTT test client (like the AWS IoT console's). Talks to device_mgmt_service over a WebSocket
// (/thing/mqtt-test-client, ADMIN only), which holds its own MQTT connection to AWS IoT.

const MAX_MESSAGES = 200;
const ALL = "__all__";
const DEFAULT_PAYLOAD = '{\n  "message": "Hello from the Mangoe admin portal"\n}';

const wsUrl = () => {
  const api = process.env.REACT_APP_DeviceAPI_URL || "";
  const base = api.replace(/\/things\/?$/, "").replace(/^http/, "ws");
  return `${base}/thing/mqtt-test-client`;
};

const freshToken = async () => {
  try {
    if (kc?.authenticated) {
      await kc.updateToken(30);
      return kc.token;
    }
  } catch {
    // fall back to the stored token
  }
  return localStorage.getItem("accessToken");
};

const pretty = (payload) => {
  try {
    return JSON.stringify(JSON.parse(payload), null, 2);
  } catch {
    return payload;
  }
};

const filterMatches = (filter, topic) => {
  const f = filter.split("/");
  const t = topic.split("/");
  for (let i = 0; i < f.length; i++) {
    if (f[i] === "#") return true;
    if (i >= t.length || (f[i] !== "+" && f[i] !== t[i])) return false;
  }
  return f.length === t.length;
};

// Ready-made device tests: optionally publish one message, and listen where the answer arrives.
const deviceTests = (thing, hub) => {
  const tests = [
    {
      key: "shadow-get",
      title: "Get shadow",
      description: "Asks AWS IoT for the current shadow; the answer comes on /get/accepted (or /get/rejected).",
      publish: `$aws/things/${thing}/shadow/get`,
      payload: "{}",
      listen: [`$aws/things/${thing}/shadow/get/+`],
    },
    {
      key: "shadow-watch",
      title: "Watch shadow updates",
      description: "Shows every reported change from the hub as it is accepted or rejected.",
      listen: [`$aws/things/${thing}/shadow/update/+`],
    },
    {
      key: "commands-watch",
      title: "Watch command replies",
      description: "Shows command acknowledgements while you control the device from the app.",
      listen: [`devices/${thing}/commands/response`],
    },
  ];
  if (hub) {
    tests.push(
      {
        key: "ota-list",
        title: "OTA: list campaigns",
        description: "Publishes as the hub to ota/<hub>/campaigns/get; the OTA Lambda answers on .../response.",
        publish: `ota/${hub}/campaigns/get`,
        payload: "{}",
        listen: [`ota/${hub}/campaigns/+/response`],
      },
      {
        key: "ota-details",
        title: "OTA: campaign details",
        description: "Asks for one campaign; replace {campaignId} in the topic.",
        publish: `ota/${hub}/campaigns/{campaignId}/get`,
        payload: '{\n  "requestType": "DETAILS"\n}',
        listen: [`ota/${hub}/campaigns/+/response`],
      },
      {
        key: "ota-url",
        title: "OTA: firmware download URL",
        description: "Asks the presigned-URL Lambda; replace {campaignId} in the topic.",
        publish: `devices/${hub}/campaigns/{campaignId}/get_presigned_url`,
        payload: "{}",
        listen: [`devices/${hub}/campaigns/+/presigned_url`],
      },
      {
        key: "hub-commands",
        title: "Watch hub commands",
        description: "Shows every command the cloud sends to the hub (incl. commands for its child devices).",
        listen: [`devices/${hub}/commands`],
      }
    );
  }
  return tests;
};

function TestCard({ test, onRun, lastRun, responses }) {
  const [topic, setTopic] = useState(test.publish || "");
  const [payload, setPayload] = useState(test.payload || "");
  const [editing, setEditing] = useState(false);
  return (
    <div className="dhc-test-card">
      <div className="dhc-test-head">
        <div>
          <div className="dhc-test-title">{test.title}</div>
          <div className="dhc-muted dhc-small">{test.description}</div>
        </div>
        <button type="button" className="dhc-btn dhc-btn-primary dhc-btn-sm" onClick={() => onRun(test, topic, payload)}>
          <PlayFill /> {test.publish ? "Send" : "Listen"}
        </button>
      </div>
      {test.publish && (
        <div className="dhc-test-line">
          <span className="dhc-test-label">Publish</span>
          {editing ? (
            <input className="dhc-input dhc-mono" value={topic} onChange={(e) => setTopic(e.target.value)} />
          ) : (
            <span className="dhc-mono">{topic}</span>
          )}
        </div>
      )}
      <div className="dhc-test-line">
        <span className="dhc-test-label">Listen</span>
        <span className="dhc-mono">{test.listen.join(", ")}</span>
      </div>
      {test.publish && editing && (
        <textarea className="dhc-input dhc-mono" rows={4} value={payload} onChange={(e) => setPayload(e.target.value)} />
      )}
      <div className="dhc-test-foot">
        {test.publish && (
          <button type="button" className="dhc-link dhc-small" onClick={() => setEditing(!editing)}>
            {editing ? "Done" : "Edit topic / payload"}
          </button>
        )}
        {lastRun && (
          <span className={`dhc-pill ${responses ? "is-pass" : "is-skipped"}`}>
            {responses ? `${responses} message${responses > 1 ? "s" : ""} received` : "Waiting for messages…"} ·{" "}
            {moment(lastRun).format("HH:mm:ss")}
          </span>
        )}
      </div>
    </div>
  );
}

function MqttTestClient() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const thing = params.get("thing") || "";

  const [conn, setConn] = useState({ state: "CONNECTING" });
  const [subs, setSubs] = useState([]); // [{filter, qos, active, paused}]
  const [messages, setMessages] = useState([]); // newest first, each {id, subscription, topic, qos, payload, receivedAt}
  const [selected, setSelected] = useState(ALL);
  const [tab, setTab] = useState(params.get("pub") ? "publish" : thing ? "tests" : "subscribe");
  const [subFilter, setSubFilter] = useState("");
  const [subQos, setSubQos] = useState(0);
  const [pubTopic, setPubTopic] = useState(params.get("pub") || "");
  const [pubPayload, setPubPayload] = useState(DEFAULT_PAYLOAD);
  const [pubQos, setPubQos] = useState(0);
  const [topics, setTopics] = useState(null);
  const [runs, setRuns] = useState({}); // test key -> {at, listen}

  const wsRef = useRef(null);
  const pending = useRef({});
  const seq = useRef(0);
  const subsRef = useRef(subs);
  subsRef.current = subs;
  const wantedRef = useRef(params.getAll("sub"));

  useEffect(() => {
    if (!thing) return;
    getDeviceMqttTopics(thing)
      .then((res) => setTopics(res.data?.data || null))
      .catch(() => setTopics(null));
  }, [thing]);

  const send = useCallback(
    (body) =>
      new Promise((resolve, reject) => {
        const ws = wsRef.current;
        if (!ws || ws.readyState !== WebSocket.OPEN) {
          reject(new Error("Not connected"));
          return;
        }
        const id = String(++seq.current);
        pending.current[id] = { resolve, reject };
        ws.send(JSON.stringify({ ...body, id }));
      }),
    []
  );

  const subscribe = useCallback(
    async (filter, qos = 0, { quiet = false } = {}) => {
      const f = filter.trim();
      if (!f) return;
      if (subsRef.current.some((s) => s.filter === f && s.active)) {
        setSelected(f);
        return;
      }
      setSubs((prev) => [...prev.filter((s) => s.filter !== f), { filter: f, qos, active: false, paused: false }]);
      try {
        await send({ type: "subscribe", topic: f, qos });
        setSubs((prev) => prev.map((s) => (s.filter === f ? { ...s, active: true } : s)));
        setSelected(f);
        if (!quiet) toast.success(`Subscribed to ${f}`);
      } catch (e) {
        setSubs((prev) => prev.filter((s) => s.filter !== f));
        toast.error(`Subscribe failed: ${e.message}`);
      }
    },
    [send]
  );

  const connect = useCallback(async () => {
    if (wsRef.current && wsRef.current.readyState <= WebSocket.OPEN) return;
    setConn({ state: "CONNECTING" });
    const token = await freshToken();
    const ws = new WebSocket(`${wsUrl()}${token ? `?access_token=${encodeURIComponent(token)}` : ""}`);
    wsRef.current = ws;

    ws.onmessage = (event) => {
      let msg;
      try {
        msg = JSON.parse(event.data);
      } catch {
        return;
      }
      if (msg.type === "status") {
        setConn(msg);
        if (msg.state === "CONNECTED") {
          // restore earlier subscriptions after a reconnect, then the ones requested in the URL
          const again = subsRef.current.map((s) => s.filter);
          setSubs([]);
          [...again, ...wantedRef.current].forEach((f) => subscribe(f, 0, { quiet: true }));
          wantedRef.current = [];
        }
      } else if (msg.type === "ack" || msg.type === "error") {
        const p = pending.current[msg.id];
        if (p) {
          delete pending.current[msg.id];
          if (msg.type === "ack") p.resolve(msg);
          else p.reject(new Error(msg.message));
        } else if (msg.type === "error") {
          toast.error(msg.message);
        }
      } else if (msg.type === "message") {
        const paused = subsRef.current.find((s) => s.filter === msg.subscription)?.paused;
        if (paused) return;
        setMessages((prev) =>
          [{ ...msg, id: `${msg.receivedAt}-${Math.random()}` }, ...prev].slice(0, MAX_MESSAGES * 5)
        );
      } else if (msg.type === "dropped") {
        toast.warn(`${msg.count} messages dropped (more than 50 per second)`);
      }
    };
    ws.onclose = (event) => {
      if (wsRef.current !== ws) return;
      Object.values(pending.current).forEach((p) => p.reject(new Error("Connection closed")));
      pending.current = {};
      setSubs((prev) => prev.map((s) => ({ ...s, active: false })));
      setConn((c) =>
        c.state === "DISABLED"
          ? c
          : {
              ...c,
              state: event.code === 1008 ? "FORBIDDEN" : "DISCONNECTED",
              message: event.code === 1008 ? "You need the ADMIN role to use the MQTT test client" : c.message,
            }
      );
    };
    ws.onerror = () => setConn((c) => ({ ...c, state: "ERROR", message: "WebSocket connection failed" }));
  }, [subscribe]);

  const disconnect = () => {
    const ws = wsRef.current;
    if (ws) ws.close();
  };

  useEffect(() => {
    connect();
    return () => {
      const ws = wsRef.current;
      wsRef.current = null;
      if (ws) ws.close();
    };
  }, [connect]);

  const unsubscribe = async (filter) => {
    try {
      await send({ type: "unsubscribe", topic: filter });
    } catch {
      // connection gone: drop it locally anyway
    }
    setSubs((prev) => prev.filter((s) => s.filter !== filter));
    setMessages((prev) => prev.filter((m) => m.subscription !== filter));
    if (selected === filter) setSelected(ALL);
  };

  const publish = async (topic, payload, qos = 0) => {
    const t = topic.trim();
    if (/[{}]/.test(t.replace(/\$aws/, ""))) {
      toast.error("Replace the {placeholder} in the topic first");
      return false;
    }
    try {
      await send({ type: "publish", topic: t, payload, qos });
      toast.success(`Published to ${t}`);
      return true;
    } catch (e) {
      toast.error(`Publish failed: ${e.message}`);
      return false;
    }
  };

  const runTest = async (test, topic, payload) => {
    for (const f of test.listen) {
      await subscribe(f, 0, { quiet: true });
    }
    if (test.publish && !(await publish(topic, payload))) return;
    setRuns((prev) => ({ ...prev, [test.key]: { at: Date.now(), listen: test.listen } }));
    setSelected(test.listen.length === 1 ? test.listen[0] : ALL);
  };

  const togglePause = (filter) =>
    setSubs((prev) => prev.map((s) => (s.filter === filter ? { ...s, paused: !s.paused } : s)));

  const shown = useMemo(
    () => (selected === ALL ? messages : messages.filter((m) => m.subscription === selected)).slice(0, MAX_MESSAGES),
    [messages, selected]
  );

  const exportMessages = () => {
    const blob = new Blob([JSON.stringify(shown, null, 2)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `mqtt-messages-${moment().format("YYYYMMDD-HHmmss")}.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied");
    } catch {
      toast.error("Could not copy to clipboard");
    }
  };

  const hub = topics?.hubThingName || (thing ? thing : null);
  const tests = thing ? deviceTests(thing, hub) : [];
  const deviceTopics = topics?.topics || [];
  const subSuggestions = Array.from(
    new Set([
      ...(thing ? [`$aws/things/${thing}/shadow/#`] : []),
      ...(hub ? [`devices/${hub}/#`, `ota/${hub}/#`] : []),
      ...deviceTopics.filter((t) => t.action === "Subscribe").map((t) => t.subscribeFilter),
      ...deviceTopics.map((t) => t.subscribeFilter),
    ])
  );
  const pubSuggestions = deviceTopics.filter((t) => t.action === "Publish").map((t) => t.topic);
  const connected = conn.state === "CONNECTED";
  const countFor = (filter) => messages.filter((m) => m.subscription === filter).length;
  const responsesFor = (run) =>
    run ? messages.filter((m) => m.receivedAt >= run.at && run.listen.some((f) => filterMatches(f, m.topic))).length : 0;

  return (
    <div className="dhc">
      <div className="dhc-page-head">
        <div>
          {thing && (
            <button type="button" className="dhc-link dhc-small" onClick={() => navigate(`/devices/health/${encodeURIComponent(thing)}?tab=mqtt`)}>
              <ArrowLeft /> Back to device health
            </button>
          )}
          <h2 className="dhc-page-title">MQTT test client</h2>
          <p className="dhc-muted dhc-small">
            Subscribe to and publish MQTT messages through AWS IoT to check that a device's topics work. Messages you
            publish reach real devices and Lambdas in this environment.
          </p>
        </div>
        {thing && (
          <span className="dhc-pill is-info dhc-mono" title="Device">
            {thing}
          </span>
        )}
      </div>

      <div className="dhc-panel dhc-conn">
        <span className={`dhc-conn-dot is-${conn.state.toLowerCase()}`} />
        <div className="dhc-conn-main">
          <b>
            {connected ? "Connected" : conn.state === "CONNECTING" ? "Connecting…" : conn.state === "DISABLED" ? "Disabled" : "Disconnected"}
          </b>
          <span className="dhc-muted dhc-small">
            {conn.message || (conn.clientId ? `Client ${conn.clientId} · ${conn.endpoint}` : "")}
          </span>
        </div>
        {connected ? (
          <button type="button" className="dhc-btn" onClick={disconnect}>
            Disconnect
          </button>
        ) : (
          <button type="button" className="dhc-btn" onClick={connect} disabled={conn.state === "CONNECTING" || conn.state === "DISABLED"}>
            Connect
          </button>
        )}
      </div>

      <div className="dhc-panel">
        <div className="dhc-tabs">
          {[
            ...(thing ? [["tests", "Device tests"]] : []),
            ["subscribe", "Subscribe to a topic"],
            ["publish", "Publish to a topic"],
          ].map(([key, label]) => (
            <button key={key} type="button" className={`dhc-tab ${tab === key ? "is-active" : ""}`} onClick={() => setTab(key)}>
              {label}
            </button>
          ))}
        </div>
        <div className="dhc-tab-body">
          {tab === "tests" && (
            <div className="dhc-test-grid">
              {tests.map((t) => (
                <TestCard key={t.key} test={t} onRun={runTest} lastRun={runs[t.key]?.at} responses={responsesFor(runs[t.key])} />
              ))}
            </div>
          )}

          {tab === "subscribe" && (
            <form
              className="dhc-form"
              onSubmit={(e) => {
                e.preventDefault();
                subscribe(subFilter, subQos);
              }}
            >
              <label className="dhc-label">
                Topic filter
                <span className="dhc-muted dhc-small"> — MQTT wildcards + (one level) and # (rest) are allowed</span>
              </label>
              <input
                className="dhc-input dhc-mono"
                list="dhc-sub-suggestions"
                value={subFilter}
                onChange={(e) => setSubFilter(e.target.value)}
                placeholder={thing ? `$aws/things/${thing}/shadow/#` : "Enter the topic filter"}
              />
              <datalist id="dhc-sub-suggestions">
                {subSuggestions.map((s) => (
                  <option key={s} value={s} />
                ))}
              </datalist>
              <div className="dhc-form-row">
                <label className="dhc-label-inline">
                  QoS
                  <select className="dhc-input dhc-select" value={subQos} onChange={(e) => setSubQos(Number(e.target.value))}>
                    <option value={0}>0 - at most once</option>
                    <option value={1}>1 - at least once</option>
                  </select>
                </label>
                <button type="submit" className="dhc-btn dhc-btn-primary dhc-btn-sm" disabled={!connected || !subFilter.trim()}>
                  Subscribe
                </button>
              </div>
            </form>
          )}

          {tab === "publish" && (
            <form
              className="dhc-form"
              onSubmit={(e) => {
                e.preventDefault();
                publish(pubTopic, pubPayload, pubQos);
              }}
            >
              <label className="dhc-label">Topic name</label>
              <input
                className="dhc-input dhc-mono"
                list="dhc-pub-suggestions"
                value={pubTopic}
                onChange={(e) => setPubTopic(e.target.value)}
                placeholder="Enter the topic name"
              />
              <datalist id="dhc-pub-suggestions">
                {pubSuggestions.map((s) => (
                  <option key={s} value={s} />
                ))}
              </datalist>
              <label className="dhc-label">Message payload</label>
              <textarea
                className="dhc-input dhc-mono"
                rows={6}
                value={pubPayload}
                onChange={(e) => setPubPayload(e.target.value)}
              />
              <div className="dhc-form-row">
                <label className="dhc-label-inline">
                  QoS
                  <select className="dhc-input dhc-select" value={pubQos} onChange={(e) => setPubQos(Number(e.target.value))}>
                    <option value={0}>0 - at most once</option>
                    <option value={1}>1 - at least once</option>
                  </select>
                </label>
                <button type="button" className="dhc-btn dhc-btn-sm" onClick={() => setPubPayload(pretty(pubPayload))}>
                  Format JSON
                </button>
                <button type="submit" className="dhc-btn dhc-btn-primary dhc-btn-sm" disabled={!connected || !pubTopic.trim()}>
                  Publish
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      <div className="dhc-messages">
        <div className="dhc-panel dhc-sub-list">
          <div className="dhc-sub-list-head">Subscriptions</div>
          <button
            type="button"
            className={`dhc-sub-item ${selected === ALL ? "is-active" : ""}`}
            onClick={() => setSelected(ALL)}
          >
            <span className="dhc-sub-name">All messages</span>
            <span className="dhc-tab-count">{messages.length}</span>
          </button>
          {subs.map((s) => (
            <div key={s.filter} className={`dhc-sub-item ${selected === s.filter ? "is-active" : ""}`} onClick={() => setSelected(s.filter)}>
              <span className={`dhc-conn-dot ${s.active ? "is-connected" : "is-connecting"}`} />
              <span className="dhc-sub-name dhc-mono" title={s.filter}>
                {s.filter}
              </span>
              <span className="dhc-tab-count">{countFor(s.filter)}</span>
              <button
                type="button"
                className="dhc-icon-btn"
                title={s.paused ? "Resume" : "Pause"}
                onClick={(e) => {
                  e.stopPropagation();
                  togglePause(s.filter);
                }}
              >
                {s.paused ? <PlayFill /> : <PauseFill />}
              </button>
              <button
                type="button"
                className="dhc-icon-btn"
                title="Unsubscribe"
                onClick={(e) => {
                  e.stopPropagation();
                  unsubscribe(s.filter);
                }}
              >
                <XLg size={11} />
              </button>
            </div>
          ))}
          {!subs.length && <div className="dhc-muted dhc-small dhc-pad">No subscriptions yet</div>}
        </div>

        <div className="dhc-panel dhc-msg-panel">
          <div className="dhc-msg-head">
            <span className="dhc-mono dhc-msg-title">{selected === ALL ? "All messages" : selected}</span>
            <div className="dhc-msg-actions">
              <button
                type="button"
                className="dhc-btn dhc-btn-sm"
                onClick={() =>
                  setMessages((prev) => (selected === ALL ? [] : prev.filter((m) => m.subscription !== selected)))
                }
              >
                <Trash size={12} /> Clear
              </button>
              <button type="button" className="dhc-btn dhc-btn-sm" onClick={exportMessages} disabled={!shown.length}>
                <Download size={12} /> Export
              </button>
            </div>
          </div>
          <div className="dhc-msg-list">
            {shown.map((m) => (
              <div key={m.id} className="dhc-msg">
                <div className="dhc-msg-meta">
                  <span className="dhc-mono dhc-msg-topic">{m.topic}</span>
                  <span className="dhc-muted dhc-small dhc-nowrap">
                    {moment(m.receivedAt).format("HH:mm:ss.SSS")} · QoS {m.qos}
                  </span>
                  <button type="button" className="dhc-icon-btn" title="Copy payload" onClick={() => copy(m.payload)}>
                    <Clipboard size={12} />
                  </button>
                </div>
                <pre className="dhc-raw dhc-msg-payload">{pretty(m.payload)}</pre>
              </div>
            ))}
            {!shown.length && (
              <div className="dhc-empty">
                {subs.length ? "No messages yet. Messages appear here as soon as they arrive." : "Subscribe to a topic to see its messages here."}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default MqttTestClient;
